import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardMedia, 
  CardContent, 
  Box, 
  Tabs, 
  Tab, 
  TextField, 
  InputAdornment, 
  Pagination, 
  CircularProgress, 
  Alert, 
  IconButton, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select 
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import axios from 'axios';

// Функция для получения ID видео из URL
const getVideoId = (url, source = 'youtube') => {
  if (!url || typeof url !== 'string') {
    console.warn('getVideoId: URL is empty or not a string');
    return null;
  }

  try {
    let videoId = null;

    // Проверяем, не является ли URL уже встроенным
    if (source === 'youtube') {
      if (url.includes('youtube.com/embed/')) {
        videoId = url.split('/').pop().split('?')[0];
        return videoId;
      }
      
      // YouTube URL patterns
      const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/i;
      const match = url.match(youtubeRegex);
      if (match && match[1]) {
        videoId = match[1];
      }
    } else if (source === 'vimeo') {
      if (url.includes('player.vimeo.com/video/')) {
        videoId = url.split('/').pop().split('?')[0];
        return videoId;
      }
      
      // Vimeo URL patterns
      const vimeoRegex = /(?:vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:[a-zA-Z0-9_-]+)?)/i;
      const match = url.match(vimeoRegex);
      if (match && match[1]) {
        videoId = match[1];
      }
    } else if (source === 'rutube') {
      // Проверяем, не является ли URL уже встроенным
      if (url.includes('rutube.ru/play/embed/')) {
        videoId = url.split('/').pop().split('?')[0];
        return videoId;
      }
      
      // Rutube URL patterns
      // Обрабатываем форматы: 
      // https://rutube.ru/video/[id]/
      // https://rutube.ru/shorts/[id]/
      // https://rutube.ru/play/embed/[id]
      const rutubeRegex = /(?:rutube\.ru\/(?:video\/|shorts\/|play\/embed\/)([a-zA-Z0-9]+))/i;
      const match = url.match(rutubeRegex);
      if (match && match[1]) {
        videoId = match[1];
      }
      
      // Если не нашли по регулярке, пробуем извлечь ID из пути URL
      if (!videoId) {
        const urlParts = url.split('/');
        // Берем последнюю часть URL после последнего слэша
        const lastPart = urlParts[urlParts.length - 1];
        if (lastPart && lastPart.length > 0) {
          // Удаляем слэш в конце, если он есть
          videoId = lastPart.replace(/\/$/, '');
        }
      }
    }
  } catch (error) {
    console.error('Ошибка при получении ID видео:', error);
  }
  
  return videoId;
  return null;
};

// Функция для получения URL для встраивания видео
const getEmbedUrl = (url, source = 'youtube') => {
  // Если URL уже является встроенным, вернуть его как есть
  if (!url || typeof url !== 'string') {
    console.warn('getEmbedUrl: URL пустой или не является строкой:', url);
    return null;
  }
  
  try {
    // Если URL уже является встроенным, вернуть его как есть
    if (url.includes('youtube.com/embed/') || 
        url.includes('player.vimeo.com/video/') || 
        url.includes('rutube.ru/play/embed/')) {
      console.log(`getEmbedUrl: URL уже является встроенным: ${url}`);
      return url;
    }

    const videoId = getVideoId(url, source);
    if (!videoId) {
      console.warn(`getEmbedUrl: Не удалось получить ID видео из URL: ${url}`);
      return null;
    }

    let embedUrl = null;
    
    if (source === 'youtube') {
      embedUrl = `https://www.youtube.com/embed/${videoId}`;
    } else if (source === 'vimeo') {
      embedUrl = `https://player.vimeo.com/video/${videoId}`;
    } else if (source === 'rutube') {
      embedUrl = `https://rutube.ru/play/embed/${videoId}`;
    } else {
      embedUrl = url; // Для других источников вернуть оригинальный URL
    }
    
    console.log(`getEmbedUrl: Сгенерирован embedUrl: ${embedUrl} для URL: ${url}`);
    return embedUrl;
  } catch (error) {
    console.error(`getEmbedUrl: Ошибка при создании URL для встраивания: ${url}`, error);
    return null;
  }
};

// Component for displaying a video card
const VideoCard = ({ video }) => {
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);

  // Обработчик загрузки превью
  const handleThumbnailLoad = () => {
    setThumbnailLoaded(true);
  };

  // Проверяем наличие URL для видео и превью
  const videoUrl = video.url || '';
  const embedUrl = video.embedUrl || getEmbedUrl(videoUrl, video.source);
  const hasEmbedUrl = embedUrl && embedUrl.trim() !== '';
  const hasThumbnail = video.thumbnailUrl && video.thumbnailUrl.trim() !== '';
  
  // Используем заглушку, если нет превью
  const thumbnailImage = hasThumbnail ? video.thumbnailUrl : '/assets/placeholders/no-video-preview.jpg';
  
  // Отладочная информация
  useEffect(() => {
    if (hasEmbedUrl) {
      console.log(`Загрузка видео "${video.title}" (ID: ${video.id}):`, {
        url: video.url,
        embedUrl: embedUrl,
        source: video.source
      });
    }
  }, [video.id, video.title, video.url, embedUrl, video.source, hasEmbedUrl]);
  
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
        {hasEmbedUrl ? (
          <Box
            component="iframe"
            src={embedUrl}
            title={video.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 0
            }}
            onError={(e) => console.error('Ошибка загрузки iframe:', e)}
          />
        ) : (
          <>
            <CardMedia
              component="img"
              image={thumbnailImage}
              alt={video.title}
              onLoad={handleThumbnailLoad}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: thumbnailLoaded ? 'block' : 'none'
              }}
            />
            {!thumbnailLoaded && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f0f0f0'
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.2)'
              }}
            >
              <Typography variant="body1" color="white">
                Видео недоступно
              </Typography>
            </Box>
          </>
        )}
      </Box>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography gutterBottom variant="h6" component="div">
          {video.title}
        </Typography>
        {video.description && (
          <Typography variant="body2" color="text.secondary">
            {video.description.length > 100
              ? `${video.description.substring(0, 100)}...`
              : video.description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const VideosPage = () => {
  // State for videos data
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for filtering and pagination
  const [currentCategory, setCurrentCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const videosPerPage = 12;

  // Fetch videos on component mount and when filters change
  useEffect(() => {
    fetchVideos();
  }, [currentCategory, searchQuery, page]);

  // Fetch videos from API
  const fetchVideos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query parameters
      const params = {
        page,
        limit: videosPerPage,
        search: searchQuery || undefined,
        category: currentCategory !== 'all' ? currentCategory : undefined
      };
      
      const response = await axios.get('/api/videos', { params });
      
      // Отладочная информация о полученных видео
      console.log('Получены данные видео:', response.data.data);
      
      // Проверяем и обрабатываем данные видео
      const processedVideos = response.data.data.map(video => {
        // Проверяем наличие обязательных полей
        if (!video.id) {
          console.warn('Видео без ID:', video);
        }
        
        if (!video.title) {
          console.warn('Видео без названия:', video);
          video.title = 'Без названия';
        }
        
        // Проверяем URL и создаем embedUrl при необходимости
        if (!video.url) {
          console.warn(`Видео "${video.title}" (ID: ${video.id}) не имеет URL`);
        } else {
          // Если нет embedUrl, пытаемся создать его из URL
          if (!video.embedUrl) {
            video.embedUrl = getEmbedUrl(video.url, video.source);
            console.log(`Создан embedUrl для видео "${video.title}":`, video.embedUrl);
          }
          
          // Проверяем наличие thumbnailUrl
          if (!video.thumbnailUrl && video.url) {
            // Используем локальную заглушку
            video.thumbnailUrl = '/assets/placeholders/no-video-preview.jpg';
            console.log(`Используем заглушку для видео "${video.title}"`);
          }
        }
        
        return video;
      });
      
      setVideos(processedVideos);
      setTotalPages(Math.ceil(response.data.total / videosPerPage));
      
      // If this is the first load, also fetch categories
      if (categories.length === 0) {
        fetchCategories();
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching videos:', error);
      setError('Ошибка при загрузке видео');
      setLoading(false);
    }
  };

  // Fetch video categories
  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/videos/categories');
      setCategories(response.data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Handle category change
  const handleCategoryChange = (event, newValue) => {
    setCurrentCategory(newValue);
    setPage(1); // Reset to first page when changing category
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(1); // Reset to first page when searching
  };

  // Handle pagination change
  const handlePageChange = (event, value) => {
    setPage(value);
    // Scroll to top when changing page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" gutterBottom align="center">
        Видеогалерея
      </Typography>
      
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Поиск видео..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{ mb: 2 }}
        />
        
        <Tabs
          value={currentCategory}
          onChange={handleCategoryChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mb: 2 }}
        >
          <Tab label="Все" value="all" />
          {categories.map((category) => (
            <Tab key={category} label={category} value={category} />
          ))}
        </Tabs>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : videos.length === 0 ? (
        <Alert severity="info">
          {searchQuery || currentCategory !== 'all'
            ? 'По вашему запросу ничего не найдено'
            : 'Видео пока не добавлены'}
        </Alert>
      ) : (
        <>
          <Grid container spacing={3}>
            {videos.map((video) => (
              <Grid item key={video._id || `video-${video.title}-${Math.random()}`} xs={12} sm={6} md={4}>
                <VideoCard key={video._id || `video-card-${video.title}-${Math.random()}`} video={video} />
              </Grid>
            ))}
          </Grid>
          
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default VideosPage;
